import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { AuthService } from './../../core/services/auth.service';
import { MenuService } from './../services/menu.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, Subscription, delay, map, switchMap, takeUntil } from 'rxjs';
// Angular
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
// RxJS
import { ErrorService } from '@uw/core/services/error.service';
import { LoadingService } from '@uw/core/interceptors/loader-spinner.service';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CampaignService } from '@uw/core/services/campaign.service';
import { CampaignStateService } from '@uw/core/services/campaign-state.service';
import { CampaignFacadeService } from '@uw/core/facades/campaign.facade.service';
import { CampaignInfo, LiteCampaignTypes } from '@uw/core/models/campaign.model';
import { environment } from '@uw-env/environment';
import { CustomNavigationService } from '@uw/core/custom-navigation.service';
import { LanguageService } from '@uw/core/language.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIconButton, MatButton } from '@angular/material/button';
import { SidebarMenuComponent } from '../sidebar-menu/sidebar-menu.component';
import { UwRouterLink } from '../../shared/directives/directives/uw-router-link.directive';
import { NgClass, AsyncPipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-base',
    templateUrl: './theme-base.component.html',
    styleUrls: ['./theme-base.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [MatProgressSpinner, MatSidenavContainer, MatSidenav, MatIconButton, RouterOutlet, SidebarMenuComponent, MatButton, UwRouterLink, NgClass, AsyncPipe, TranslateModule]
})
export class ThemeBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('drawer')
  mainSidebarMenu: MatSidenav;
  openSidebar = false;
  menuConfig: any;
  error$: Observable<string | null>;
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  loading: boolean = false;
  mainHeaderLogo$!: Observable<string>;
  mainHeaderLogo2$!: Observable<string>;
  // mainHeaderLogo2$!: Observable<{logo2: string, displayLogo2: boolean, displayDefaultLogo: boolean}>;
  mainHeaderLogoAlt$!: Observable<string>;
  mainHeaderLogo2Alt$!: Observable<string>;
  footerLinks$!: Observable<SafeHtml>;
  footerCopyrights$!: Observable<SafeHtml>;
  campaignGuid!: string;
  hideNav: boolean = false;
  showCampaignSwitcher$: Observable<boolean>;
  showLangSwitcher: boolean;
  destroy$ = new Subject();
  hideOnlyRightNav$: Observable<boolean>;
  showCampaignSpecificLogos$: Observable<boolean>;
  currentCampaign: CampaignInfo;
  donorCampaignList: CampaignInfo[];
  readonly currentLanguage = this.translateService.currentLang;

  siteType = environment.mode;

  // readonly showMyCampaignLink$ = combineLatest([
  //   this.campaignService.getMyCampaignDetails(
  //     this.campaignStateService.getSelectedCampaign().campaignGuid
  //   ),
  //   this.campaignService
  //     .getCampaingInfo(this.campaignStateService.getSelectedCampaign().campaignGuid)
  //     .pipe(map((data) => data.type.isCampaignClosed)),
  // ]).pipe(
  //   map(
  //     ([myCampaignDetail, isCampaignClosed]) =>
  //       !isCampaignClosed &&
  //       (myCampaignDetail.isThermometerVisible ||
  //         myCampaignDetail.isGroupThermometerVisible)
  //   )
  // );

  constructor(
    public errorService: ErrorService,
    private router: Router,
    public authService: AuthService,
    private campaignService: CampaignService,
    private campaignStateService: CampaignStateService,
    private translateService: TranslateService,
    private customNavigationService: CustomNavigationService,
    private menuService: MenuService,
    private campaignFacadeService: CampaignFacadeService,
    private _loading: LoadingService,
    private _activatedRoute: ActivatedRoute,
    private languageService: LanguageService,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef
  ) {
    this.error$ = this.errorService.getError().pipe(
      delay(0),
      map((err) => (err ? translateService.instant(err) : null))
    );
  }

  /**
   * On init
   */
  ngOnInit(): void {
    const loggedInUser = this.authService.getUser();

    const ignoreLoggedInUserLangPref = localStorage.getItem(
      'ignoreLoggedInUserLangPref'
    );

    if (
      loggedInUser &&
      loggedInUser.language &&
      ignoreLoggedInUserLangPref != 'yes'
    ) {
      const lang = loggedInUser.language.split('-')[0];
      this.translateService.use(lang);
      localStorage.setItem('lang', lang);
      // Once the user lang is set it can be ignored to avoid infinite looping on reload.
      localStorage.setItem('ignoreLoggedInUserLangPref', 'yes');

      // Not a good practice to reload the entire app. If the language is not getting set for lang specific apis,
      // then the api fetch should be trigerred using observables rather than full app reload.
      // The app reload was causing issues with closed campaign page context getting displayed
      // and re-drawn due to app reload. However why that component was rendered before this parent component was rendered has not yet been identified. Jul 11, 2024
      window.location.reload();

    }

    this.listenToLoading();
    this.showCampaignSwitcher$ = this.authService.getUserState().pipe(
      switchMap((_user) => this.campaignFacadeService.showCampaignSwitcher$),
      delay(0)
    );
    this.hideNav = this.menuService.hideNav;
    this.hideOnlyRightNav$ = this.menuService.hideOnlyRightNav$.pipe(delay(0));
    this.showCampaignSpecificLogos$ =
      this.menuService.showCampaignSpecificLogos$.pipe(delay(0));
    this.campaignGuid =
      this.campaignStateService.getSelectedCampaign()?.campaignGuid ?? localStorage.getItem('current-campaign') ?? '';

    this.languageService.showLanguageSwitcher$
      .pipe(
        map((showSwitcher) => {


          let multipleLangExists = false;

          if (this.campaignStateService.getSelectedCampaign() && this.campaignStateService.getSelectedCampaign()?.profile) {
            multipleLangExists =  this.campaignStateService.getSelectedCampaign()?.profile?.languages
            ?.length > 1;
            if (multipleLangExists){
              localStorage.setItem('multi-lang', "yes");
            } else {
              localStorage.removeItem('multi-lang');
            }
          } else {
            if (localStorage.getItem('multi-lang')){
              multipleLangExists = true;
            }
          }

          this.showLangSwitcher = multipleLangExists && showSwitcher;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.campaignService
      .getPageContent(this.campaignGuid ?? '', 'generic')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (!data.has('Logo_1')) {
          this.campaignService.companyLogo$.next('');
          this.campaignService.companyLogoAlt$.next('');
        }

        if (!data.has('Logo_2')) {
          this.campaignService.companyLogo2$.next('');
          this.campaignService.companyLogo2Alt$.next('');
        }
      });

    this.mainHeaderLogo$ = this.campaignService.companyLogo().pipe(delay(0));
    this.mainHeaderLogo2$ = this.campaignService.companyLogo2().pipe(delay(0));

    // this.mainHeaderLogo2$ = combineLatest([this.campaignService.companyLogo2(), this.mainHeaderLogo$]).pipe(
    //   map(([logo2, logo1]) => {
    //     // let displayDefaultLogo = (logo1 && !logo2) ? true:false;
    //     let displayDefaultLogo = false;
    //     let displayLogo2 = (logo2 )? true: false;
    //     return {logo2, displayLogo2, displayDefaultLogo}
    //   })
    // );

    this.mainHeaderLogoAlt$ = this.campaignService.companyLogoAlt().pipe(delay(0));
    this.mainHeaderLogo2Alt$ = this.campaignService.companyLogo2Alt().pipe(delay(0));

    this.footerLinks$ = this.campaignService.footerLinks().pipe(delay(0), map(html => this.sanitizer.bypassSecurityTrustHtml(html)));
    this.footerCopyrights$ = this.campaignService.footerCopyright().pipe(delay(0), map(html => this.sanitizer.bypassSecurityTrustHtml(html)));
    this.campaignFacadeService
      .getCurrentCampaign()
      .pipe(takeUntil(this.destroy$))
      .subscribe((c) => (this.currentCampaign = c));
    this.campaignFacadeService
      .getDonorCampaignList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.donorCampaignList = list));
  }

  onRouterOutletActivate() {
    this.openSidebar = true;
    this.cdRef.detectChanges();
  }

  onRouterOutletDeactivate() {
    this.openSidebar = false;
    this.cdRef.detectChanges();
  }

  ngAfterViewInit() {
    this.menuService.donarSubMenuClick().subscribe((toggle) => {
      if (toggle && !this.mainSidebarMenu.opened) {
        this.mainSidebarMenu.open();
      } else {
        this.mainSidebarMenu.close();
      }
    });
  }

  switchToAdminSite() {

    let adminSiteUrl = environment.adminSiteURL;

    // if (token) {
    //   adminSiteUrl +=
    //     '?lang=' + localStorage.getItem('lang');
    // }

    adminSiteUrl +=
    '?access=switch&lang=' + localStorage.getItem('lang');

    window.location.href = adminSiteUrl;
  }

  onSignInClick() {
    const isSSOUser = this.authService.userLoggedIn$.value.isSSOUser;

    let loginRoute = 'main/login';

    if (isSSOUser) {
      loginRoute = 'main/sso-login';
    }

    this.router.navigate([loginRoute]);
  }

  onSideMenuClick() {
    if (this.authService.getUser()) {
      if (
        this.router.url.includes('/donor/') ||
        this.router.url.includes('/donateur/')
      ) {
        this.customNavigationService.navigate(
          [
            '/donor',
            this.campaignStateService.getSelectedCampaign()?.campaignGuid,
            { outlets: { sidebar: ['landing'] } },
          ],
          { queryParamsHandling: 'preserve' }
        );
      } else if (this.router.url.includes('/info/')) {
        this.customNavigationService.navigate(
          ['/info', { outlets: { sidebar: ['landing'] } }],
          { queryParamsHandling: 'preserve' }
        );
      } else {
        this.customNavigationService.navigate(
          [this.router.url, { outlets: { sidebar: ['landing'] } }],
          { queryParamsHandling: 'preserve' }
        );
      }
    } else {
      this.customNavigationService.navigate(
        [
          '/donor',
          this.campaignStateService.getSelectedCampaign()?.campaignGuid,
          { outlets: { sidebar: ['login'] } },
        ],
        { queryParamsHandling: 'preserve' }
      );
    }
  }

  onSidebarClose() {
    this.customNavigationService.navigate(
      [
        {
          outlets: {
            sidebar: null,
          },
        },
      ],
      {
        relativeTo: this._activatedRoute, // <--- PARENT activated route.
      }
    );
  }

  onChangeCampaign() {
    if (this.donorCampaignList && this.donorCampaignList.length > 1) {
      this.customNavigationService.navigate([
        '/main',
        'campaign',
        'select-campaign',
      ]);
    } else if (
      this.currentCampaign &&
      this.currentCampaign.liteCampaignType === LiteCampaignTypes.Kiosk
    ) {
      this.customNavigationService.navigate([
        '/main',
        'campaign',
        this.currentCampaign.campaignGuid,
        'select-kiosk',
      ]);
    } else if (
      this.currentCampaign &&
      this.currentCampaign.liteCampaignType === LiteCampaignTypes.National
    ) {
      this.customNavigationService.navigate([
        '/main',
        'campaign',
        this.currentCampaign.campaignGuid,
        'select-uw',
      ]);
    } else {
      this.customNavigationService.navigate(['/main/campaign/select-campaign']);
    }
  }

  listenToLoading(): void {
    this._loading.loadingSub
      .pipe(delay(0)) // This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
      .subscribe((loading) => {
        this.loading = loading;
      });
  }

  changeLanguage(e: Event) {
    e.preventDefault();
    const curLang = this.translateService.currentLang;
    localStorage.setItem('lang', curLang == 'en' ? 'fr' : 'en');

    const loggedInUser = this.authService.getUser();

    if (loggedInUser) {
      localStorage.setItem('ignoreLoggedInUserLangPref', 'yes');
    }

    if (this.translateService.currentLang == 'en') {
      window.location.href =
        localStorage.getItem('baseUrl') +
        this.customNavigationService.replaceSubUrlToFrenchRoute(
          window.location.pathname
        ) +
        window.location.search;
    } else {
      window.location.href =
        localStorage.getItem('baseUrl') +
        this.customNavigationService.replaceSubUrlToEnglishRoute(
          window.location.pathname
        ) +
        window.location.search;
    }
  }

  onDismiss() {
    this.errorService.resetError();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
